import type { DotLottie } from '@lottiefiles/dotlottie-react'
import { DotLottieReact } from '@lottiefiles/dotlottie-react'
import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'
import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { paths } from 'src/config/paths'
import onboardingComplete from 'src/shared/assets/lottie/onboarding-complete.lottie'
import Portal from 'src/shared/components/utils/portals/portal'
import AuthUsersImage from 'src/shared/features/users/assets/home/auth-modal/user-pfps.png'
import { cn } from 'src/shared/utils/cn'

import { DirectionArrowShort } from '../../../assets/direction-arrow-short'
import ConsumerLoginModal from '../auth-forms/login'
import ConsumerSignupModal from '../auth-forms/signup'

export const SuccessToast = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const [isHovered, setIsHovered] = useState(false)
  const [lottieRef, setLottieRef] = useState<DotLottie | null>(null)

  useEffect(() => {
    if (lottieRef) {
      lottieRef.play()
    }
  }, [lottieRef])

  useEffect(() => {
    if (isOpen && !isHovered) {
      const timer = setTimeout(() => {
        onClose()
      }, 1500)

      return () => clearTimeout(timer)
    }

    return () => {
      if (lottieRef) {
        lottieRef.stop()
      }
    }
  }, [isOpen, isHovered, onClose])

  const handleMouseEnter = () => setIsHovered(true)
  const handleMouseLeave = () => {
    setIsHovered(false)
    // Restart timer when mouse leaves
    setTimeout(() => {
      if (!isHovered) {
        onClose()
      }
    }, 1500)
  }

  const toastVariants = {
    hidden: {
      y: 100,
      x: '-50%',
      opacity: 0,
      scale: 0.8,
    },
    visible: {
      y: 0,
      x: '-50%',
      opacity: 1,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 25,
        duration: 0.6,
      },
    },
    exit: {
      y: 100,
      x: '-50%',
      opacity: 0,
      scale: 0.8,
      transition: {
        duration: 0.3,
        ease: 'easeInOut',
      },
    },
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed bottom-4 left-1/2 z-[60]"
          variants={toastVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <motion.div
            className="flex h-[55px] w-[300px] items-center justify-center gap-3 rounded-full border border-border bg-white px-6 py-4 sm:w-[350px]"
            style={{
              boxShadow: '0px 2px 30px 1px rgba(27, 48, 41, 0.12)',
            }}
            whileHover={{ scale: 1.02 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          >
            <div className="relative size-[30px]">
              <DotLottieReact
                dotLottieRefCallback={setLottieRef}
                src={onboardingComplete}
                autoplay
                speed={1}
                style={{ transform: 'scale(1.5)' }}
              />
            </div>
            <span className="text-sm font-semibold text-primary">Account successfully created!</span>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

const AuthModal = ({ open, onClose }: { flow: 'signup' | 'login'; open: boolean; onClose: () => void }) => {
  const [view, setView] = useState<'login' | 'signup'>('login')
  const navigate = useNavigate()
  const [showSuccessToast, setShowSuccessToast] = useState(false)

  const signInAsProfessional = () => {
    navigate(paths.auth.login.getHref())
  }

  const close = () => {
    setView('login')
    onClose()
  }

  const onSuccess = () => {
    close()
    setShowSuccessToast(true)
    // Navigate to client's home page
    navigate(paths.client.index.getHref(), { replace: true, state: { auth_success: true } })
  }

  const professionalText = useMemo(() => {
    return view === 'login' ? 'I am a professional' : 'Join as a professional'
  }, [view])

  return (
    <>
      <Portal>
        <div
          className={cn(
            'fixed inset-0 bottom-0 left-0 right-0 top-0 z-50 flex flex-col items-center justify-center gap-4 bg-black/50 transition-opacity duration-300 lg:flex-row lg:gap-8',
            open ? 'opacity-100' : 'pointer-events-none opacity-0',
          )}
        >
          <div className="z-[51] flex max-h-[90vh] w-[450px] flex-col gap-5 overflow-y-auto py-8">
            <div className="relative rounded-[20px] bg-white p-12">
              {view === 'login' ? (
                <ConsumerLoginModal onSuccess={onSuccess} onSwitchView={() => setView('signup')} />
              ) : view === 'signup' ? (
                <ConsumerSignupModal onSuccess={onSuccess} onSwitchView={() => setView('login')} />
              ) : null}
              <button
                type="button"
                onClick={close}
                className="absolute right-6 top-6 flex size-[35px] flex-col items-center justify-center gap-1 rounded-full bg-white text-center text-sm text-primary"
                style={{
                  boxShadow: '0px 0px 7px 1px rgba(27,48,41,0.1)',
                }}
              >
                <X className="size-5" />
              </button>
            </div>
            <div
              className="group/auth-modal flex min-h-[75px] cursor-pointer flex-row items-center justify-between gap-5 rounded-[20px] bg-accent px-5 transition-all duration-150 hover:bg-primary"
              onClick={signInAsProfessional}
            >
              <div className="flex flex-row items-center justify-center gap-3">
                <img src={AuthUsersImage} alt="user profile images" className="h-[50px] w-[80px] object-contain" />
                <h5 className="text-[20px] font-bold text-primary group-hover/auth-modal:text-white">
                  {professionalText}
                </h5>
              </div>
              <button
                type="button"
                className="flex size-[40px] shrink-0 items-center justify-center rounded-[40px] bg-primary text-sm text-white group-hover/auth-modal:bg-white group-hover/auth-modal:text-primary lg:text-base"
              >
                <DirectionArrowShort className="size-[18px]" />
              </button>
            </div>
          </div>
          <div className="fixed inset-0 z-[48]" onClick={close} />
        </div>
        <SuccessToast isOpen={showSuccessToast} onClose={() => setShowSuccessToast(false)} />
      </Portal>
    </>
  )
}

export default AuthModal
